import { useCallback, useState } from 'react';
import { copyToClipboard } from '../helpers/clipboard';

// useShare — single hook for "share this link" actions.
//
// On mobile (iOS Safari, Android Chrome, Edge): opens the platform's native
// share sheet via navigator.share. The Web Share API uses transient user
// activation (~5s window after a user tap) so it survives one async hop —
// which our flow needs because we mint the share token over the network
// AFTER the tap.
//
// On desktop or anywhere navigator.share isn't available: falls back to
// clipboard write. This path requires the call to be synchronous with the
// user gesture, which on iOS Safari is impossible after the await in the
// caller — that's exactly the "Couldn't copy" toast clients reported on
// their iPhones (SMRTSTATS-297 follow-up).
//
// Returns:
//   share({ url, title?, text? })  → one of:
//     'shared'    — native share sheet completed (mobile)
//     'cancelled' — user dismissed the native share sheet (no toast)
//     'copied'    — clipboard write succeeded (desktop fallback)
//     'failed'    — everything failed
//   status: latest non-cancel result, sticks for 2s for toast UI
//   reset(): clear status immediately
export const useShare = () => {
  const [status, setStatus] = useState(null);

  const setStatusWithAutoClear = useCallback((result) => {
    setStatus(result);
    setTimeout(() => {
      setStatus((cur) => (cur === result ? null : cur));
    }, 2000);
  }, []);

  const share = useCallback(async ({ url, title, text } = {}) => {
    const target = url || (typeof window !== 'undefined' ? window.location.href : '');
    if (!target) {
      setStatusWithAutoClear('failed');
      return 'failed';
    }

    // Prefer the Web Share API. iOS Safari, Android Chrome, Edge, and PWAs
    // installed to the home screen all support it. Opens the platform's
    // native share sheet — better UX than a silent clipboard copy.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          url: target,
          ...(title ? { title } : {}),
          ...(text ? { text } : {}),
        });
        setStatusWithAutoClear('shared');
        return 'shared';
      } catch (err) {
        // User dismissed the sheet — quiet, no error toast.
        if (err?.name === 'AbortError') {
          return 'cancelled';
        }
        // Anything else (permission denied, no targets) — fall through to
        // clipboard, which might still succeed on some browsers.
      }
    }

    const ok = await copyToClipboard(target);
    const result = ok ? 'copied' : 'failed';
    setStatusWithAutoClear(result);
    return result;
  }, [setStatusWithAutoClear]);

  const reset = useCallback(() => setStatus(null), []);

  return { share, status, reset };
};

export default useShare;
